import {
  createCacheKey,
  ENDPOINTS,
  fetchPlainResource,
  fetchResource,
  handleErrorResponse,
  useCreatePayload,
} from 'hammock';
import deepClone from 'lodash/cloneDeep';
import { nanoid } from 'nanoid';
import React, { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useDefaultVariants } from '@candu-ai/styleguide/application-variants';
import { ComponentType, VariantData, VariantRuleStylesheet } from '@candu-ai/styleguide/types';

import { getCSStyles } from './getCssStyles';

export type UpdateVariantsRules = {
  componentType: string;
  variant: string;
  rule: string;
  items: {
    [key: string]: {
      value?: string;
      imports?: string;
    };
  };
};

export type UpdateVariantsName = {
  componentType: string;
  variant: string;
  name: string;
};

export type CloneVariant = {
  componentType: string;
  variant: string;
};

export type DeleteVariant = {
  componentType: string;
  variant: string;
};

type ContextValue = {
  variants?: VariantRuleStylesheet;
  variables?: Record<string, string>;
  updateRules?: (payload: UpdateVariantsRules) => Promise<VariantData | undefined>;
  updateName?: (payload: UpdateVariantsName) => Promise<VariantData | undefined>;
  cloneVariant?: (payload: CloneVariant) => Promise<VariantData | undefined>;
  deleteVariant?: (payload: DeleteVariant) => Promise<VariantData | undefined>;
  save?: () => Promise<unknown>;
  undo?: () => void;
  redo?: () => void;
  resynchronizeData?: (payload: VariantData) => void;
  loading: boolean;
  error: boolean;
  isSaving: boolean;
  errorSaving?: unknown;
};

export const StyleguideContext = React.createContext<ContextValue>({
  loading: false,
  error: false,
  isSaving: false,
});

export type StyleguideDataProps = {
  children: React.ReactNode;
};

export const StyleguideData: React.FC<StyleguideDataProps> = React.memo(
  ({ children }: StyleguideDataProps) => {
    const queryClient = useQueryClient();
    const { create, getAll } = useCreatePayload(ENDPOINTS.styleguideVariants);

    const versionsQueries = getAll({});

    const activeVersion = queryClient.getQueryData<number>(['styleguide-editor', 'active-version']);
    const versions = queryClient.getQueriesData<number>(createCacheKey(versionsQueries));

    const savedIteration = React.useRef<number>(activeVersion ?? 0);
    const iterations = React.useRef<number[]>(
      versions.length === 0
        ? [0]
        : versions.filter((v) => v[0].length === 4).map((_, index) => index), // only variants queries with iteration number (comes from SG editor)
    );

    // cacheKey that are used to retrieve live variants in the editor
    const usedVariantQueriesCacheKeyArray = versions
      .filter((v) => v[0].length === 3)
      .map(([queryKey]) => queryKey.toString());

    // TODO => make this hammock compatible
    const { get: getBaseStylesheet } = useCreatePayload(`/api/styleguide-processor` as ENDPOINTS);

    const { mutateAsync: saveVariants, isLoading: isSaving, error: errorSaving } = useMutation(
      ['save-me'],
      async (updatedVariants: VariantRuleStylesheet) => {
        const savePayload = create({
          body: updatedVariants,
        });
        return fetchResource(savePayload).catch((e) => handleErrorResponse(e, savePayload));
      },
      {
        onSuccess: () => {
          savedIteration.current = changeIteration;
          queryClient.setQueryData(['styleguide-editor', 'active-version'], changeIteration);

          queryClient.invalidateQueries({
            predicate: (query) =>
              usedVariantQueriesCacheKeyArray.includes(query.queryKey.toString()),
          });
        },
      },
    );

    const [changeIteration, setChangeIteration] = useState<number>(savedIteration.current);

    const {
      data,
      isLoading: loading,
      isError: error,
      cacheKey: cacheKeyVariants,
    } = useDefaultVariants({
      iteration: changeIteration,
    });

    // const variants = useMemo(() => data?.variants, [data]);
    const { variants } = data || {};
    const payloadBaseStylesheet = getBaseStylesheet({
      id: `base-stylesheet.css`,
    });
    const { data: baseStylesheet } = useQuery(createCacheKey(payloadBaseStylesheet), () =>
      fetchPlainResource(payloadBaseStylesheet),
    );

    const iterateOnSuccess = (updatedData: VariantData) =>
      setChangeIteration((oldIteration) => {
        iterations.current = [...iterations.current.slice(0, oldIteration + 1), oldIteration + 1];

        queryClient.setQueryData([...cacheKeyVariants, `${oldIteration + 1}`], updatedData);
        return oldIteration + 1;
      });

    const { mutateAsync: updateRules } = useMutation(
      ['update-rules'],
      async (updatedRules: UpdateVariantsRules) => {
        const oldData: VariantData | undefined = queryClient.getQueryData([
          ...cacheKeyVariants,
          `${changeIteration}`,
        ]);
        if (oldData?.variants) {
          const bumpedData = deepClone(oldData.variants);
          const { rule, componentType, variant, items } = updatedRules;
          const newProperties = {
            ...bumpedData?.[componentType][variant].selectors,
          };

          Object.entries(items).forEach(([key, { value, imports }]) => {
            if (value) {
              newProperties[key] = {
                ...newProperties[key],
                rules: {
                  ...(newProperties[key]?.rules || {}),
                  [rule]: value,
                },
              };
            }
            if (imports && imports !== 'unset') {
              newProperties[key] = {
                ...newProperties[key],
                imports: {
                  ...(newProperties[key]?.imports || {}),
                  [rule]: imports,
                },
              };
            } else if (imports === 'unset') {
              newProperties[key] = {
                ...newProperties[key],
                imports: undefined,
              };
            }
          });

          bumpedData[componentType][variant].selectors = newProperties;
          return { ...oldData, variants: bumpedData } as VariantData;
        }
        return undefined;
      },
      {
        onSuccess: iterateOnSuccess,
      },
    );

    const { mutateAsync: updateName } = useMutation(
      async (updatedName: UpdateVariantsName) => {
        const oldData: VariantData | undefined = queryClient.getQueryData([
          ...cacheKeyVariants,
          `${changeIteration}`,
        ]);
        if (oldData?.variants) {
          const bumpedData = deepClone(oldData.variants);
          const { name, componentType, variant } = updatedName;

          bumpedData[componentType][variant].name = name;
          return { ...oldData, variants: bumpedData } as VariantData;
        }
        return undefined;
      },
      {
        onSuccess: iterateOnSuccess,
      },
    );

    const { mutateAsync: cloneVariant } = useMutation(
      async (clonedVariant: CloneVariant) => {
        const oldData: VariantData | undefined = queryClient.getQueryData([
          ...cacheKeyVariants,
          `${changeIteration}`,
        ]);
        if (oldData?.variants) {
          const bumpedData = deepClone(oldData.variants);

          const { componentType, variant } = clonedVariant;

          const variantToClone = deepClone(bumpedData[componentType][variant]);
          const newVariant = nanoid(6);
          bumpedData[componentType][newVariant] = {
            ...variantToClone,
            name: `Copy of ${variantToClone.name}`,
          };
          return { ...oldData, variants: bumpedData, newVariantId: newVariant } as VariantData;
        }
        return undefined;
      },
      {
        onSuccess: iterateOnSuccess,
      },
    );

    const { mutateAsync: deleteVariant } = useMutation(
      async (clonedVariant: CloneVariant) => {
        const oldData: VariantData | undefined = queryClient.getQueryData([
          ...cacheKeyVariants,
          `${changeIteration}`,
        ]);
        if (oldData?.variants) {
          const bumpedData = deepClone(oldData.variants);

          const { componentType, variant } = clonedVariant;
          delete bumpedData[componentType][variant];

          return { ...oldData, variants: bumpedData } as VariantData;
        }
        return undefined;
      },
      {
        onSuccess: iterateOnSuccess,
      },
    );

    /**
     * Pass a VariantRuleStyleSheet to add it in the stack of the RQ state manager.
     * Handy when the data is modified externally
     */
    const resynchronizeData = useCallback(
      (newData: VariantData) => {
        iterateOnSuccess(newData);

        savedIteration.current = changeIteration;
        queryClient.setQueryData(
          ['styleguide-editor', 'active-version'],
          (oldIteration: number) => {
            const newIteration = oldIteration + 1;
            savedIteration.current = newIteration;
            return newIteration;
          },
        );
      },
      [iterateOnSuccess, queryClient],
    );

    // eslint-disable-next-line consistent-return
    const save = useCallback(async () => {
      if (variants) {
        return saveVariants(variants);
      }
    }, [variants, saveVariants]);

    const undo = useCallback(() => {
      setChangeIteration((oldIteration) => {
        return oldIteration - 1;
      });
    }, []);

    const redo = useCallback(() => {
      setChangeIteration((oldIteration) => {
        return oldIteration + 1; // then clear iteration
      });
    }, []);

    const canUndo = changeIteration > 0;
    const canRedo = iterations.current.length - 1 > changeIteration;
    const canSave = savedIteration.current !== changeIteration;

    const variables = useMemo(
      () => (variants ? variants[ComponentType.Global].base.selectors.global.rules : {}),
      [variants],
    );

    const context = useMemo(
      () => ({
        variants,
        variables,
        loading,
        error,
        isSaving,
        errorSaving,

        resynchronizeData,
        updateRules,
        updateName,
        cloneVariant,
        deleteVariant,
        save: canSave ? save : undefined,
        undo: canUndo ? undo : undefined,
        redo: canRedo ? redo : undefined,
      }),
      [
        variants,
        loading,
        error,
        isSaving,
        resynchronizeData,
        errorSaving,
        variables,
        updateRules,
        save,
        updateName,
        cloneVariant,
        deleteVariant,
        canSave,
        canUndo,
        canRedo,
        undo,
        redo,
      ],
    );

    const cssGlobalVariables = useMemo(
      () => (variables ? `:root,:host{${getCSStyles(variables ?? {})}` : ''),
      [variables],
    );

    return (
      <StyleguideContext.Provider value={context}>
        {/* Inject base stylesheet (no variant here) */}
        <style type="text/css">{baseStylesheet ?? ''}</style>
        {/* Inject global variables */}
        <style type="text/css">{cssGlobalVariables ?? ''}</style>

        {children}
      </StyleguideContext.Provider>
    );
  },
);
